import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Subject, Subscription, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';

import {
  WHAutocompleteScrollComponent,
  WHDropdownModule,
  WHDropdownItemDOM,
  WHMatDateTimePickerModule,
  WHFeatureKeyENUM,
  WHIconENUM,
  WHLoginDataService,
  WHTeamDataService,
  WHTeamDOM,
  WHTranslationTypeENUM,
  IWHLocationDTO,
  WHWorkObjectDataService,
  WHToastService,
} from '@workheld/workheld-shared-lib';

import { WHButtonComponent } from 'src/app/app-shared/components/buttons/w-h-button/w-h-button.component';
import { WHButtonGroupComponent } from 'src/app/app-shared/components/buttons/w-h-button-group/w-h-button-group.component';

import { IAppStore } from 'src/app/app-stores/index.stores';
import { workheldCustomerPreviewStoreFeatureKey } from 'src/app/app-stores/w-h-customer-stores/w-h-customer-preview-store/w-h-customer-preview-store.reducer';
import { GetWHCustomerPreviewDtoWithParams } from 'src/app/app-stores/w-h-customer-stores/w-h-customer-preview-store/w-h-customer-preview-store.actions';
import { EquipmentStore } from 'src/app/app-modules-pages/equipment/stores/equipment-table.store';
import { WHPipesModule } from 'src/app/app-shared/shared-pipes/wh-pipes.module';
import { NgFormBuilderHelperService } from 'src/app/app-services-helper/ng-form-builder-helper.service';
import { WHAgmCoreLocationFormModule } from 'src/app/app-modules-pages/w-h-agm-core-util/w-h-agm-core-location-form/w-h-agm-core-location-form.module';
import { environment } from 'src/environments/environment';

export interface ProjectQuickCreatePayload {
  title: string;
  extId?: string;
  description?: string;
  customerId?: string;
  equipmentId?: string;
  teamId?: string;
  category?: string;
  targetState?: string;
  startDate?: string;
  endDate?: string;
  location?: IWHLocationDTO;
  workOrderTitle?: string;
  workOrderExtId?: string;
  workOrderDescription?: string;
  workOrderCategory?: string;
  workStepTitle?: string;
  workStepExtId?: string;
  workStepDescription?: string;
}

@Component({
  selector: 'app-mat-dialog-project-quick-create',
  templateUrl: './mat-dialog-project-quick-create.component.html',
  styleUrls: ['./mat-dialog-project-quick-create.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    TranslateModule,
    WHPipesModule,
    WHAutocompleteScrollComponent,
    WHButtonComponent,
    WHButtonGroupComponent,
    WHDropdownModule,
    WHMatDateTimePickerModule,
    WHAgmCoreLocationFormModule,
  ],
})
export class MatDialogProjectQuickCreateComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<IAppStore>>(Store);
  private readonly teamDataService = inject(WHTeamDataService);
  private readonly equipmentStore = inject(EquipmentStore);
  private readonly loginDataService = inject(WHLoginDataService);
  private readonly ngFormBuilderHelperService = inject(
    NgFormBuilderHelperService,
  );
  private readonly workObjectDataService = inject(WHWorkObjectDataService);
  private readonly toastService = inject(WHToastService);
  readonly matDialogRef = inject(
    MatDialogRef<MatDialogProjectQuickCreateComponent>,
  );

  readonly ENUMS = WHTranslationTypeENUM;
  readonly projectIcon = WHIconENUM.ProjectIcon as string;
  saving = signal(false);
  createAnother = signal(false);
  isLocationEnabled = signal(false);
  clearAutocompletes = signal(false);
  customerInitialValue = signal('');
  clearEquipment$ = new Subject<void>();
  locationFormGroup: FormGroup;

  // Form
  form = new FormGroup({
    title: new FormControl('', Validators.required),
    extId: new FormControl(''),
    description: new FormControl(''),
    customerId: new FormControl(''),
    equipmentId: new FormControl(''),
    teamId: new FormControl(''),
    category: new FormControl(''),
    targetState: new FormControl(''),
    workOrderExtId: new FormControl(''),
    workOrderTitle: new FormControl(''),
    workOrderDescription: new FormControl(''),
    workOrderCategory: new FormControl(''),
    workStepExtId: new FormControl(''),
    workStepTitle: new FormControl(''),
    workStepDescription: new FormControl(''),
  });

  // Date picker form group
  dateFormGroup = new FormGroup({
    startDateForm: new FormControl(null),
    endDateForm: new FormControl(null),
  });

  // Customer
  customerData$ = this.store.select(workheldCustomerPreviewStoreFeatureKey);
  customersLoading$ = new BehaviorSubject(false);

  // Equipment
  equipmentData$ = toObservable(this.equipmentStore.equipments);
  equipmentLoading$ = toObservable(this.equipmentStore.isLoading);

  // Team
  teamNameFormControl = new FormControl('');
  teamDropdownItemDomList: WHDropdownItemDOM[] = [];
  teamDropdownLoading = false;

  private selectedEquipmentCustomerId: string | null = null;
  private subscriptions: Subscription[] = [];

  constructor() {
    this.locationFormGroup =
      this.ngFormBuilderHelperService.initLocationFormGroup();

    effect(() => {
      this.equipmentStore.loadEquipments(this.equipmentStore.filters());
    });
  }

  ngOnInit(): void {
    this.loadCustomers({ term: '', page: 0 });

    this.subscriptions.push(
      this.loginDataService.featureConfigMap$.subscribe((features) => {
        this.isLocationEnabled.set(
          features.get(WHFeatureKeyENUM.CORE_WORK_FLOW_WORK_ORDER_LOCATION) ??
            false,
        );
      }),
    );
    this.equipmentStore.resetStore();

    this.subscriptions.push(
      this.customerData$.subscribe((data) => {
        this.customersLoading$.next(data?.loading ?? false);
      }),
    );

    this.subscriptions.push(
      this.teamDataService.teams$.subscribe((teamDOMList) => {
        if (!teamDOMList || !Array.isArray(teamDOMList)) return;
        this.teamDropdownItemDomList = [];
        teamDOMList.forEach((teamDOM: WHTeamDOM) => {
          if (!teamDOM?.name) return;
          const dropdownItemDOM = new WHDropdownItemDOM().fromTeamDOM(teamDOM);
          this.teamDropdownItemDomList.push(dropdownItemDOM);
        });
      }),
    );
  }

  loadCustomers(event: { term: string; page: number }): void {
    this.store.dispatch(
      new GetWHCustomerPreviewDtoWithParams({
        page: event.page,
        term: event.term,
        sort: 'name',
        sortMode: 'ASC',
      }),
    );
  }

  loadEquipments(event: { term: string; page: number }): void {
    this.equipmentStore.setPage(event.page);
    this.equipmentStore.setSearchTerm(event.term);
    this.equipmentStore.setSortMode('ASC');
    this.equipmentStore.setSort('name');
  }

  onCustomerSelect(customer: any): void {
    const customerId: string = customer?.id ?? '';
    this.customerInitialValue.set('');
    this.form.controls.customerId.patchValue(customerId);
    this.filterEquipmentsByCustomer(customerId);

    if (
      customerId &&
      this.selectedEquipmentCustomerId &&
      this.selectedEquipmentCustomerId !== customerId
    ) {
      this.clearEquipment$.next();
    }
  }

  onEquipmentSelect(equipment: any): void {
    this.form.controls.equipmentId.patchValue(equipment?.id ?? '');
    this.selectedEquipmentCustomerId = equipment?.customerId ?? null;

    const customerId: string | undefined = equipment?.customerId;
    if (customerId && customerId !== this.form.controls.customerId.value) {
      this.form.controls.customerId.patchValue(customerId);
      this.customerInitialValue.set(equipment.customerName ?? '');
      this.filterEquipmentsByCustomer(customerId);
    }
  }

  private filterEquipmentsByCustomer(customerId: string): void {
    this.equipmentStore.setPage(0);
    this.equipmentStore.setCustomerId(customerId || undefined);
  }

  onTeamEvent(event: any): void {
    if (event?.payload?.id) {
      this.form.controls.teamId.patchValue(event.payload.id);
      this.teamNameFormControl.patchValue(event.payload.name);
    }
  }

  submit(): void {
    if (this.form.invalid) return;

    const payload: ProjectQuickCreatePayload = {
      title: this.form.value.title,
      extId: this.form.value.extId || undefined,
      description: this.form.value.description || undefined,
      customerId: this.form.value.customerId || undefined,
      equipmentId: this.form.value.equipmentId || undefined,
      teamId: this.form.value.teamId || undefined,
      category: this.form.value.category || undefined,
      targetState: this.form.value.targetState || undefined,
      startDate: this.dateFormGroup.value.startDateForm || undefined,
      endDate: this.dateFormGroup.value.endDateForm || undefined,
      workOrderTitle: this.form.value.workOrderTitle || undefined,
      workOrderExtId: this.form.value.workOrderExtId || undefined,
      workOrderDescription: this.form.value.workOrderDescription || undefined,
      workOrderCategory: this.form.value.workOrderCategory || undefined,
      workStepTitle: this.form.value.workStepTitle || undefined,
      workStepExtId: this.form.value.workStepExtId || undefined,
      workStepDescription: this.form.value.workStepDescription || undefined,
    };

    if (this.isLocationEnabled()) {
      const locationDTO: IWHLocationDTO = this.locationFormGroup.value;
      if (
        locationDTO?.latitude ||
        locationDTO?.longitude ||
        locationDTO?.formattedAddress
      ) {
        payload.location = locationDTO;
      }
    }

    this.saving.set(true);
    this.subscriptions.push(
      this.workObjectDataService
        .createProjectHierarchy(
          {
            apiUrl: environment.apiUrl,
          },
          payload,
        )
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.saving.set(false);
            return throwError(() => error);
          }),
        )
        .subscribe((result) => {
          this.saving.set(false);
          this.showSuccessToast();
          if (this.createAnother()) {
            this.resetForm();
          } else {
            this.matDialogRef.close(result);
          }
        }),
    );
  }

  private resetForm(): void {
    this.form.reset();
    this.dateFormGroup.reset();
    this.locationFormGroup.reset();
    this.teamNameFormControl.reset();
    this.customerInitialValue.set('');
    this.selectedEquipmentCustomerId = null;
    this.clearAutocompletes.set(true);
    setTimeout(() => this.clearAutocompletes.set(false));
  }

  private showSuccessToast(): void {
    this.toastService.success('project.ui.createsuccess.notification');
  }

  cancel(): void {
    this.matDialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
